/**
 * The evidence-based evaluation (0099) as the recruiter sees it — shared by the candidate review and
 * the interviews hub.
 *
 * What it must never blur:
 *   - a competency with INSUFFICIENT EVIDENCE has no number. It is not a 0 and not a weakness: the
 *     interview simply didn't show it, which is a gap in coverage, not in capability;
 *   - capability (the weighted mean of evidenced competencies) and coverage (how much of the
 *     criteria had evidence at all) are two facts, shown side by side;
 *   - an evaluation judged against an older version of the criteria says so, and is not ranked;
 *   - a failed re-evaluation never replaced the current result, and the page says it failed.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import type { AnswerResult, CompetencyAction, CompetencyResult, CoverageBreakdown, EvidenceLevel } from "@talenthop/shared";
import { LIMITED_COVERAGE, MATCH_CLASS_LABEL, evaluationSummaryLine, matchClassOf } from "@talenthop/shared";
import type { AuthenticityBand, MatchClass } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { Alert, Check, Clock, Info, RefreshCw } from "../../components/icons.js";

export interface EvaluationSummary {
  id: string;
  status: "succeeded" | "failed";
  trigger: "interview_completed" | "re_evaluation" | "comparison";
  isCurrent: boolean;
  assessmentSetVersion: number;
  capability: number | null;
  coverage: number | null;
  reason: string | null;
  error: string | null;
  triggeredBy: string | null;
  judgeModel: string | null;
  createdAt: string;
}

export interface ReportEvaluation extends EvaluationSummary {
  currentSetVersion: number | null;
  outdated: boolean;
  breakdown: CoverageBreakdown;
  competencies: CompetencyResult[];
  answers: AnswerResult[];
  recommendation: string | null;
  provenance: {
    judgeProvider: string | null;
    judgeModel: string | null;
    judgeModelResolved: string | null;
    promptSha256: string | null;
    textSource: string | null;
    madeCurrentAt: string | null;
  };
  /** Capability with presence folded in when the Owner switch is on; else equal to capability. */
  overall?: number | null;
  presence?: number | null;
  presenceBasis?: { browser: boolean; camera: boolean; gaze?: boolean };
  presenceInScore?: boolean;
}

export interface ReportAssessment {
  version: number;
  competencies: { id: string; label: string; weight: number; source: "library" | "custom" }[];
}

export const LEVEL_META: Record<EvidenceLevel, { label: string; pill: string; title: string }> = {
  direct: { label: "Direct evidence", pill: "pill v", title: "The candidate described doing this exact kind of work themselves." },
  transferable: {
    label: "Transferable evidence",
    pill: "pill t",
    title: "Comparable work from another product, domain or setting — real capability, not this exact experience.",
  },
  weak: { label: "Weak evidence", pill: "pill n", title: "Generic or thin — a claim with little concrete detail behind it." },
  none: {
    label: "Insufficient evidence",
    pill: "pill n",
    title: "The interview did not give enough evidence to judge this. Not a zero and not a weakness — a gap in coverage.",
  },
};

/** One readable reason: an isolated evaluation reports the same provider error once per call. */
function shortError(err: string): string {
  const reasons = [...new Set(err.replace(/^Evaluator degraded:\s*/, "").split(/;\s*/).map((r) => r.replace(/^evaluateInterview(\([^)]*\))?:\s*/, "")))];
  const text = reasons.join("; ");
  return text.length > 220 ? `${text.slice(0, 220)}…` : text;
}

const tone = (s: number): "" | "mid" | "low" => (s >= 75 ? "" : s >= 55 ? "mid" : "low");
export const pct = (x: number | null | undefined): string => (x == null ? "—" : `${Math.round(x * 100)}%`);

/**
 * The headline label, from the ONE shared definition (matchClassOf): coverage is part of it, so a
 * high capability over a small share of the criteria reads "Limited evidence", never a strong match.
 */
export function matchMetaOf(capability: number | null, coverage: number | null): { cls: MatchClass; label: string; tone: "v" | "r" | "f" | "n" } {
  const cls = matchClassOf(capability, coverage);
  const tone = cls === "strong" ? "v" : cls === "moderate" ? "r" : cls === "low" ? "f" : "n";
  return { cls, label: MATCH_CLASS_LABEL[cls], tone };
}

/** The evaluation card's headline — evidence, never a hiring verdict; integrity failure first. */
export function verdictOf(
  capability: number | null,
  coverage: number | null,
  band: AuthenticityBand,
  breakdown: { evidenced: number; total: number; substantiated?: number },
): { text: string; tone: "v" | "r" | "f" } {
  if (band === "failed") return { text: "Integrity check failed — review the recording", tone: "f" };
  const cls = matchClassOf(capability, coverage);
  if (cls === "no_evidence") return { text: "No competency had enough evidence to judge", tone: "r" };
  if (cls === "limited_evidence") {
    return { text: `Limited evidence — ${breakdown.substantiated ?? breakdown.evidenced} of ${breakdown.total} competencies backed by concrete evidence`, tone: "r" };
  }
  if (cls === "strong") return { text: "Strong evidence across the criteria", tone: "v" };
  if (cls === "moderate") return { text: "Mixed evidence — worth a closer look", tone: "r" };
  return { text: "Weak evidence on these criteria", tone: "r" };
}

/** Capability + how many competencies the interview could assess — the two are never merged into one.
 *  (The coverage percentage is in the tooltip; on the card it reads "3 of 5 competencies assessed".) */
export function CapabilityCoverage({
  e,
  showLine = true,
}: {
  e: Pick<ReportEvaluation, "capability" | "coverage" | "breakdown">;
  /** false where the same sentence is already shown (the verdict box carries the recommendation). */
  showLine?: boolean;
}): ReactNode {
  const limited = e.coverage != null && e.coverage < LIMITED_COVERAGE;
  const total = e.breakdown.direct + e.breakdown.transferable + e.breakdown.weak + e.breakdown.insufficient;
  const assessed = total - e.breakdown.insufficient;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div className="mut sm" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span>
          Capability <b className="tnum" style={{ color: "var(--ink)" }}>{e.capability ?? "—"}</b>
          {e.capability != null && "/100"}
        </span>
        <span title={`Coverage ${pct(e.coverage)}: the share of the criteria (by weight) backed by concrete evidence — the candidate's own work, or comparable work described specifically. Generic or unsupported claims are scored but do not count.`}>
          <b className="tnum" style={{ color: limited ? "var(--warn)" : "var(--ink)" }}>{assessed} of {total}</b> competencies assessed
        </span>
      </div>
      {showLine && (
        <p className="sm" style={{ margin: 0 }}>
          {evaluationSummaryLine(e.capability, e.coverage ?? 0, e.breakdown)}
        </p>
      )}
    </div>
  );
}

/** Every competency of the evaluated set: score (or "not enough to judge"), a one-line reason, and the
 *  quoted evidence tucked behind "Show quotes" — closed by default so the page reads as a result, not a
 *  pile of evidence. No weights or strength labels here: the score is what the recruiter reads. */
/** Advice labels for a competency. `pill` reuses the existing pill tones (v good, r warn, n neutral). */
const ACTION_META: Record<CompetencyAction, { label: string; pill: string }> = {
  advance: { label: "Advance", pill: "v" },
  probe_further: { label: "Probe further", pill: "r" },
  not_enough: { label: "Not enough to judge", pill: "n" },
};

export function CompetencyEvidenceList({
  competencies,
  onQuestion,
}: {
  competencies: CompetencyResult[];
  onQuestion?: (seq: number) => void;
}): ReactNode {
  if (competencies.length === 0) {
    return (
      <p className="mut sm" style={{ margin: 0 }}>
        No competencies were evaluated.
      </p>
    );
  }
  return (
    <div className="t1-rv-comps">
      {competencies.map((c) => {
        const insufficient = c.status === "insufficient_evidence";
        return (
          <div key={c.competencyId} className="t1-rv-comp">
            <div className="t1-rv-comp-h">
              <span className="t1-rv-comp-name">{c.label}</span>
              {insufficient && !c.action && (
                <span className="pill n" title={LEVEL_META.none.title}>
                  Not enough to judge
                </span>
              )}
              <span className="t1-rv-comp-n tnum">{c.score ?? "—"}</span>
            </div>
            {c.score != null ? (
              <div className="t1-bt lg">
                <i className={tone(c.score)} style={{ width: `${c.score}%` }} />
              </div>
            ) : (
              <div className="t1-bt lg neutral" title={LEVEL_META.none.title}>
                <i style={{ width: 0 }} />
              </div>
            )}
            {c.action ? (
              <div className="t1-rv-advice">
                <span className={`pill ${ACTION_META[c.action].pill}`}>{ACTION_META[c.action].label}</span>
                {c.advice && <p className="sm">{c.advice}</p>}
              </div>
            ) : (
              c.summary && (
                <p className="sm" style={{ margin: "6px 0 0", color: "var(--ink)" }}>
                  {c.summary}
                </p>
              )
            )}
            {c.evidence.length > 0 && (
              <details className="t1-rv-evid">
                <summary>Show quotes</summary>
                {c.evidence.map((x, i) => (
                  <div key={i} style={{ marginTop: 6 }}>
                    {x.seq != null && (
                      <span className="mut sm">
                        {onQuestion ? (
                          <button type="button" className="t1-link" onClick={() => onQuestion(x.seq!)}>
                            Q{x.seq}
                          </button>
                        ) : (
                          `Q${x.seq}`
                        )}
                      </span>
                    )}
                    {x.span ? <blockquote>“{x.span}”</blockquote> : <div className="mut sm">Quote withheld on a redacted share.</div>}
                  </div>
                ))}
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The banners above an evaluation: out of date, queued, last re-evaluation failed. */
export function EvaluationNotices({
  evaluation,
  lastFailed,
  pending,
}: {
  evaluation: ReportEvaluation | null;
  lastFailed: EvaluationSummary | null;
  pending: boolean;
}): ReactNode {
  return (
    <>
      {pending && (
        <div className="banner info" style={{ margin: 0 }}>
          <Clock />
          <div>
            <b>Evaluation queued.</b> The existing answers are being evaluated — nothing is asked of the
            candidate. If the AI provider is busy it is retried automatically; this page updates when it lands.
          </div>
        </div>
      )}
      {evaluation?.outdated && (
        <div className="banner warn" style={{ margin: 0 }}>
          <Alert />
          <div>
            <b>Evaluated against criteria v{evaluation.assessmentSetVersion}; the requirement is now on v
            {evaluation.currentSetVersion}.</b> This result is kept and shown, but is not ranked against
            candidates evaluated on the current criteria. Re-evaluate the same interview to bring it up to
            date — no new interview needed.
          </div>
        </div>
      )}
      {lastFailed && !pending && (
        <div className="banner crit" style={{ margin: 0 }}>
          <Alert />
          <div>
            <b>The latest evaluation attempt failed</b> ({new Date(lastFailed.createdAt).toLocaleString()}) and
            did not replace {evaluation ? "the result shown" : "anything"}.
            {lastFailed.error ? <span className="mut" title={lastFailed.error}> {shortError(lastFailed.error)}</span> : null}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Re-evaluate THIS interview against the requirement's current criteria (POST
 * /candidacies/{id}/evaluations). Queued; the page polls the report until a new evaluation appears
 * — a success becomes current, a failure is listed and never replaces the current one.
 */
export function ReEvaluateControl({
  candidacyId,
  latestId,
  refetch,
}: {
  candidacyId: string;
  latestId: string | null;
  refetch: () => Promise<{ latest: string | null }>;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [waiting, setWaiting] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current != null) window.clearInterval(timer.current);
    },
    [candidacyId],
  );
  const mutation = useMutation({
    mutationFn: () => api.post(`/candidacies/${candidacyId}/evaluations`, { reason }),
    onSuccess: () => {
      setOpen(false);
      setReason("");
      setWaiting(true);
      const baseline = latestId;
      let attempts = 0;
      timer.current = window.setInterval(() => {
        attempts += 1;
        void refetch().then(({ latest }) => {
          if ((latest && latest !== baseline) || attempts >= 36) {
            if (timer.current != null) window.clearInterval(timer.current);
            timer.current = null;
            setWaiting(false);
          }
        });
      }, 2500);
    },
  });
  if (waiting) {
    return (
      <div className="sm" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <i className="btn-spin" style={{ borderColor: "color-mix(in srgb, var(--teal) 30%, transparent)", borderTopColor: "var(--teal)" }} />
        Evaluating the existing answers…
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        <RefreshCw /> Re-evaluate
      </button>
    );
  }
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <textarea
        className="rq-in"
        style={{ minHeight: 50, fontFamily: "inherit", fontSize: 12.5 }}
        placeholder="Reason (required, recorded with the evaluation) — e.g. 'added Business Central to the criteria'"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="btn sm primary" disabled={mutation.isPending || reason.trim().length < 4} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Queuing…" : "Re-evaluate this interview"}
        </button>
        <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      <span className="mut sm">
        Uses the answers already given — the candidate is not contacted. A failed run never replaces the current
        result.
      </span>
      {mutation.isError && (
        <span style={{ color: "var(--crit)" }} className="sm">
          {mutation.error instanceof ApiError ? mutation.error.message : "Couldn't queue the re-evaluation."}
        </span>
      )}
    </div>
  );
}

/**
 * Re-score the candidate's stored CV against the requirement as it stands now (POST
 * /candidacies/{id}/resume/rescore). Shown only when the org's Rescore switch is on and the report
 * says `canRescore`. Synchronous: one scan is charged on success; a provider failure changes nothing
 * and costs nothing (the old score stands). The score it replaces is kept and listed.
 */
export function ResumeRescoreControl({
  candidacyId,
  onDone,
}: {
  candidacyId: string;
  onDone: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ overall: number | null; previous: number | null; charged: number; degraded: boolean }>(
        `/candidacies/${candidacyId}/resume/rescore`,
        { reason },
      ),
    onSuccess: (res) => {
      setOpen(false);
      setReason("");
      setNote(
        res.degraded
          ? "Couldn't score the resume just now — the old score stands and you weren't charged. Try again shortly."
          : `Re-scored: ${res.previous ?? "—"}% → ${res.overall}%. One resume scan was used.`,
      );
      onDone();
    },
  });
  if (!open) {
    return (
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        <button type="button" className="btn sm" style={{ justifySelf: "start" }} onClick={() => { setNote(null); setOpen(true); }}>
          <RefreshCw /> Rescan resume
        </button>
        {note && <span className="mut sm">{note}</span>}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
      <textarea
        className="rq-in"
        style={{ minHeight: 50, fontFamily: "inherit", fontSize: 12.5 }}
        placeholder="Reason (required, audited) — e.g. 'requirement now needs Business Central'"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className="btn sm primary" disabled={mutation.isPending || reason.trim().length < 8} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Scanning…" : "Rescan this resume"}
        </button>
        <button type="button" className="btn sm ghost" disabled={mutation.isPending} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      <span className="mut sm">
        Scores the CV on file against the requirement as it is now. Uses one resume scan; the current score is kept
        in the history. Nobody is invited, rejected or contacted.
      </span>
      {mutation.isError && (
        <span style={{ color: "var(--crit)" }} className="sm">
          {mutation.error instanceof ApiError ? mutation.error.message : "Couldn't re-score the resume."}
        </span>
      )}
    </div>
  );
}

/**
 * Score a CV that is on file but has no score yet (POST /requisitions/{id}/resumes/{scanId}/match —
 * the same call as "Run AI match" on the Resumes hub). Once per resume; one scan is charged on
 * success, a provider failure costs nothing and can be retried.
 */
export function ResumeScoreControl({
  requisitionId,
  scanId,
  onDone,
}: {
  requisitionId: string;
  scanId: string;
  onDone: () => void;
}): ReactNode {
  const [note, setNote] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ scan: { overall: number | null }; charged: number; degraded: boolean }>(
        `/requisitions/${requisitionId}/resumes/${scanId}/match`,
        {},
      ),
    onSuccess: (res) => {
      setNote(
        res.degraded
          ? "Couldn't score the resume just now — you weren't charged. Try again shortly."
          : `Scored — ${res.scan.overall}% against the requirement.`,
      );
      onDone();
    },
  });
  return (
    <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
      <button type="button" className="btn sm primary" style={{ justifySelf: "start" }} disabled={mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? "Scoring…" : "Score this resume"}
      </button>
      {note && <span className="mut sm">{note}</span>}
      {mutation.isError && (
        <span style={{ color: "var(--crit)" }} className="sm">
          {mutation.error instanceof ApiError ? mutation.error.message : "Couldn't score the resume."}
        </span>
      )}
    </div>
  );
}

const TRIGGER_LABEL: Record<EvaluationSummary["trigger"], string> = {
  interview_completed: "After the interview",
  re_evaluation: "Re-evaluation",
  comparison: "Comparison run",
};

/** Every evaluation of this candidacy, newest first — current marked, failures shown as failures. */
export function EvaluationHistory({ items }: { items: EvaluationSummary[] }): ReactNode {
  if (items.length === 0) {
    return (
      <p className="mut sm" style={{ margin: 0 }}>
        No evaluations yet.
      </p>
    );
  }
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {items.map((h) => (
        <div key={h.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", border: "1px solid var(--line-soft)", borderRadius: 8 }}>
          <span style={{ display: "inline-flex", width: 15, marginTop: 2, color: h.status === "failed" ? "var(--crit)" : h.isCurrent ? "var(--good)" : "var(--ink-mute)" }}>
            {h.status === "failed" ? <Alert /> : h.isCurrent ? <Check /> : <Info />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sm" style={{ fontWeight: 600 }}>
              {TRIGGER_LABEL[h.trigger]} · criteria v{h.assessmentSetVersion}
              {h.isCurrent && <span className="pill v" style={{ marginLeft: 6 }}>current</span>}
              {h.status === "failed" && <span className="pill r" style={{ marginLeft: 6 }}>failed</span>}
            </div>
            <div className="mut" style={{ fontSize: 11.5 }}>
              {new Date(h.createdAt).toLocaleString()}
              {h.triggeredBy ? ` · ${h.triggeredBy}` : ""}
              {h.judgeModel ? ` · ${h.judgeModel}` : ""}
              {h.status === "succeeded" && ` · capability ${h.capability ?? "—"} · coverage ${pct(h.coverage)}`}
            </div>
            {h.reason && <div className="sm">“{h.reason}”</div>}
            {h.error && <div className="sm" style={{ color: "var(--crit)" }}>{h.error}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
